import React, { FC, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import Button from '../../../../components/button/index';
import Loader from '../../../../components/loader/index';
import { PlaceholderBar } from '../../../../components/loading-skeleton/index';
import { ExpandedTableRowsPanel } from '../../../../components/table-animations/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useSelectedPoolsStore } from '../../../../context/selected-pools.store';
import { Pool, useScreenSize, useSelectedCompany } from '../../../../hooks/index';
import { PlanStakeholder, Stakeholder } from '../../../../types/stakeholder.types';
import { ScrollablePoolPlanItem, StickyPoolPlanItem } from './pool-plan-item';

// Skeleton rows shown inside an expanded stakeholder while its pools lazy-load, so the
// expanded area is not blank on first open.
const StickyExpandedRowsSkeleton: FC = () => (
  <>
    {Array.from({ length: 1 }).map((_, index) => (
      <tr className="flex h-[72px] w-full animate-pulse bg-white" key={index}>
        <td className="h-[72px] w-[56px] border-b border-b-gray-100" />
        <td className="flex h-[72px] flex-1 items-center gap-3 border-b border-b-gray-100 px-6">
          <PlaceholderBar className="size-8 shrink-0 rounded-full bg-gray-100" />
          <PlaceholderBar className="h-4 w-40 rounded bg-gray-100" />
        </td>
      </tr>
    ))}
  </>
);

const ScrollableExpandedRowsSkeleton: FC = () => (
  <>
    {Array.from({ length: 1 }).map((_, index) => (
      <tr className="h-[72px] w-full animate-pulse border-b border-b-gray-100 bg-white" key={index}>
        {Array.from({ length: 8 }).map((_cell, cellIndex) => (
          <td className="p-4" key={cellIndex}>
            <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
          </td>
        ))}
        <td className="relative" />
      </tr>
    ))}
  </>
);

type StickyStakeholderItemProps = {
  date: Date;
  isSelected: boolean;
  stakeholder: Stakeholder;
};

export const StickyStakeholderItem: FC<StickyStakeholderItemProps> = ({
  stakeholder,
  isSelected,
  date,
}) => {
  const { scrollableTarget } = useScreenSize();
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id || '';

  const {
    stakeholderState,
    setSelectedStakeholder,
    deselectStakeholder,
    setSelectedStakeholderPool,
  } = useSelectedPoolsStore();
  const [isExpandedVisual, setExpandedVisual] = useState(false);

  const handlePoolSelected = () => {
    setSelectedStakeholder(stakeholder.id);
    setSelectedStakeholderPool('', '');
  };

  const handlePoolUnselected = () => {
    deselectStakeholder(stakeholder.id);
  };

  const { pools, fetchNextPage, hasNextPage, isEmpty, isLoading } = Pool.usePoolsByStakeholder({
    companyId,
    stakeholderId: stakeholder?.id || '',
    vestByDate: date,
    enabled: isSelected || isExpandedVisual,
  });

  return (
    <>
      <tr
        className={twMerge(
          'flex h-[72px] w-full border-l-[2px] border-l-white bg-white transition-colors',
          isSelected && 'border-l-brand-700 bg-brand-25',
        )}
      >
        <td className="relative h-[72px] border-b-[1px] border-b-gray-100">
          <div className="flex h-full w-full items-center justify-center">
            {stakeholder && pools && (
              <Button
                className="h-[72px] p-4 transition-colors"
                onClick={isSelected ? handlePoolUnselected : handlePoolSelected}
                styleType="NONE"
              >
                <ChevronDownIcon
                  className={twMerge('transition-transform', isSelected ? 'rotate-180' : '')}
                  iconColor={isSelected ? '#2565C8' : '#344054'}
                />
              </Button>
            )}
          </div>
        </td>
        <td className="flex h-[72px] w-full items-center justify-between gap-4 overflow-hidden text-nowrap border-b-[1px] border-b-gray-100 px-6 py-4">
          <div className="flex max-w-[300px] items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
              {stakeholder.fullName[0]}
            </div>
            <Tooltip content={stakeholder.fullName}>
              <span className="truncate text-sm font-[450] text-gray-700">
                {stakeholder.fullName}
              </span>
            </Tooltip>
          </div>
        </td>
      </tr>
      {!isEmpty && pools && (
        <ExpandedTableRowsPanel
          contentKey={isLoading ? 'loading' : 'loaded'}
          isExpanded={isSelected}
          onExpandedVisualChange={setExpandedVisual}
          syncColumns={false}
        >
          {(isSelected || isExpandedVisual) && isLoading ? (
            <StickyExpandedRowsSkeleton />
          ) : (
            (isSelected || isExpandedVisual) && (
              <InfiniteScroll
                dataLength={pools.length}
                hasMore={hasNextPage}
                loader={<Loader />}
                next={fetchNextPage}
                scrollableTarget={scrollableTarget}
              >
                {pools.map((pool) => (
                  <StickyPoolPlanItem
                    companyId={companyId}
                    date={date}
                    isSelected={stakeholderState.stakeholderPoolId.some(
                      (item) => item.stakeholderId === stakeholder.id && item.poolId === pool.id,
                    )}
                    key={pool.id}
                    pool={pool}
                    stakeholderId={stakeholder.id}
                  />
                ))}
              </InfiniteScroll>
            )
          )}
        </ExpandedTableRowsPanel>
      )}
    </>
  );
};

export type ScrollableStakeholderItemProps = {
  date: Date;
  isSelected: boolean;
  onDeleteClick: (id: string) => void;
  onDeletePlanClick: (id: string) => void;
  onEditClick: (id: string) => void;
  onEditPlanClick: (id: string) => void;
  onEditTerminationDateClick: (id: string) => void;
  onViewClick: (id: string) => void;
  onViewPlanClick: (id: string) => void;
  stakeholder: PlanStakeholder;
};

export const ScrollableStakeholderItem: FC<ScrollableStakeholderItemProps> = ({
  stakeholder,
  isSelected,
  onEditClick,
  onViewClick,
  onDeleteClick,
  onEditPlanClick,
  onViewPlanClick,
  onDeletePlanClick,
  onEditTerminationDateClick,
  date,
}) => {
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id || '';

  const { stakeholderState } = useSelectedPoolsStore();
  const [isExpandedVisual, setExpandedVisual] = useState(false);

  const granted = stakeholder?.granted?.toLocaleString('en-US');
  const totalGrantVested = stakeholder?.totalGrantVested?.toLocaleString('en-US');
  const sharesVested = stakeholder?.sharesVested?.toLocaleString('en-US');
  const exercisable = stakeholder?.exercisable?.toLocaleString('en-US');
  const exercised = stakeholder?.exercised?.toLocaleString('en-US');
  const expired = stakeholder?.expired?.toLocaleString('en-US');
  const cashSettled = stakeholder.cashSettled?.toLocaleString('en-US');
  const returnedToPool = stakeholder.returnedToPool?.toLocaleString('en-US');

  const { pools, isEmpty, isLoading } = Pool.usePoolsByStakeholder({
    companyId,
    stakeholderId: stakeholder?.id || '',
    vestByDate: date,
    enabled: isSelected || isExpandedVisual,
  });

  return (
    <>
      <tr
        className={twMerge(
          'h-[72px] w-full border-b-[1px] border-b-gray-100 bg-white transition-colors',
          isSelected && 'bg-brand-25',
        )}
      >
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !granted && 'text-gray-300',
            )}
          >
            {granted || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !totalGrantVested && 'text-gray-300',
            )}
          >
            {totalGrantVested || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !sharesVested && 'text-gray-300',
            )}
          >
            {sharesVested || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !exercisable && 'text-gray-300',
            )}
          >
            {exercisable || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !exercised && 'text-gray-300',
            )}
          >
            {exercised || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !cashSettled && 'text-gray-300',
            )}
          >
            {cashSettled || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !returnedToPool && 'text-gray-300',
            )}
          >
            {returnedToPool || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !expired && 'text-gray-300',
            )}
          >
            {expired || '-'}
          </span>
        </td>
        <td className="relative"></td>
      </tr>
      {!isEmpty && pools && (
        <ExpandedTableRowsPanel
          contentKey={isLoading ? 'loading' : 'loaded'}
          isExpanded={isSelected}
          onExpandedVisualChange={setExpandedVisual}
        >
          {(isSelected || isExpandedVisual) && isLoading ? (
            <ScrollableExpandedRowsSkeleton />
          ) : (
            (isSelected || isExpandedVisual) &&
            pools.map((pool) => (
              <ScrollablePoolPlanItem
                companyId={companyId}
                date={date}
                isSelected={stakeholderState.stakeholderPoolId.some(
                  (item) => item.stakeholderId === stakeholder.id && item.poolId === pool.id,
                )}
                key={pool.id}
                onDeleteClick={onDeleteClick}
                onDeletePlanClick={onDeletePlanClick}
                onEditClick={onEditClick}
                onEditPlanClick={onEditPlanClick}
                onEditTerminationDateClick={onEditTerminationDateClick}
                onViewClick={onViewClick}
                onViewPlanClick={onViewPlanClick}
                pool={pool}
                stakeholderId={stakeholder.id}
              />
            ))
          )}
        </ExpandedTableRowsPanel>
      )}
    </>
  );
};
